import { Component, computed, inject, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { AutomationsStore } from '@kinet-open-network/client/automations/data-access';
import { DetailBaseComponent } from '@kinet-open-network/client/core/detail-base';
import { formBuilder } from '@kinet-open-network/client/core/forms/ui';
import {
  DeliveriesService,
  selectLazyDeliveries,
} from '@kinet-open-network/client/deliveries/data-access';
import {
  buildFlowActionMenuItems,
  FLOW_TYPE_CONFIGS,
} from '@kinet-open-network/client/flows/data-access';
import {
  confirmDialog,
  HeaderButton,
  SidebarLayoutComponent,
  SidebarTabsConfig,
  toast,
} from '@kinet-open-network/client/shared/ui';
import {
  automationActionByContext,
  automationActionTypeOptions,
  isSidebarAction,
  OrderStatus,
} from '@kinet-open-network/shared/data-access/model';
import {
  FlowOrderType,
  FlowType,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { MenuItem } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { filter, firstValueFrom, skip, Subscription, take } from 'rxjs';
import { FlowOrders } from '../../data-access/flow-orders.model';
import { FlowOrdersService } from '../../data-access/flow-orders.service';
import { FlowOrdersStore } from '../../data-access/flow-orders.store';

@Component({
  selector: 'kon-flow-order-detail',
  imports: [SidebarLayoutComponent, RouterModule, TabsModule],
  template: `
    <kon-sidebar-layout
      [actions]="actions"
      [buttons]="headerButtons()"
      [style]="{ width: '100rem', 'max-width': '99%' }"
      [tabs]="tabsConfig"
    />
  `,
})
export class FlowOrderDetailComponent extends DetailBaseComponent<FlowOrders> {
  store = inject(FlowOrdersStore);
  service = inject(FlowOrdersService);
  automationsStore = inject(AutomationsStore);
  private deliveriesService = inject(DeliveriesService);
  private flowConfig = inject(FLOW_TYPE_CONFIGS, { optional: true })?.find(
    (c) => c.type === FlowType.order,
  );
  model = this.store.model;

  tabsConfig: SidebarTabsConfig = {
    tabs: [
      {
        value: 'default',
        label: 'Všeobecné',
        routerLink: ['.'],
      },
      {
        value: 'documents',
        label: 'Dokumenty',
        routerLink: ['documents'],
        visible: () => this.showDocumentsTab(),
      },
      {
        value: 'audits',
        label: 'Audit',
        routerLink: ['audits'],
        visible: () => !this.isCreate(),
      },
    ],
  };

  showDocumentsTab = computed(
    () => !this.isCreate() && this.ability().can('read', 'Document'),
  );

  actions: MenuItem[] = [
    {
      label: 'Prepočítať poplatky za prepravu',
      icon: 'pi pi-refresh',
      command: () => this.refreshFees(),
      visible: true,
    },
    {
      label: 'Aktualizovať ceny',
      icon: 'pi pi-dollar',
      command: () => this.refreshPrices(),
      visible: true,
    },
    ...(automationActionByContext['order'] || [])
      .filter(isSidebarAction)
      .map((actionType) => {
        const options = automationActionTypeOptions.get(actionType);
        return {
          label: options?.label,
          icon: options?.icon,
          command: () =>
            this.automationsStore.runAction(actionType, 'order', {
              orderId: this.model()?.id,
            }),
          visible: !!this.model()?.id && this.ability().can('update', 'Order'),
        };
      }),
    ...buildFlowActionMenuItems(this.store, this.flowConfig, this.ability()),
  ];

  private async refreshFees(patchModel = true): Promise<any[]> {
    const model = this.model();
    if (!model?.items?.length) return [];

    try {
      const res = await firstValueFrom(this.service.getOrderFees(model as any));

      if (!res?.data) {
        toast({ severity: 'info', detail: 'Žiadne položky s poplatkom' });
        return [];
      }

      if (patchModel) {
        this.store.patchModel({ items: res.data } as Partial<FlowOrders>);
      }
      toast({
        severity: 'success',
        detail: 'Poplatky za prepravu boli aktualizované',
      });

      return res.data;
    } catch {
      toast({ severity: 'danger', detail: 'Nepodarilo sa načítať poplatky' });
      return [];
    }
  }

  private async refreshPrices(): Promise<void> {
    const customerId = this.customerId();
    const date = this.dateDocument();
    if (!this.model()?.items?.length || !customerId || !date) return;

    try {
      await this.store.refreshAllItemPrices(customerId, date);
      toast({ severity: 'success', detail: 'Ceny boli aktualizované' });
    } catch {
      toast({ severity: 'danger', detail: 'Nepodarilo sa aktualizovať ceny' });
    }
  }

  private customerId = computed(() => this.model()?.customerId);
  private dateDocument = computed(() => {
    const date = this.model()?.date;
    return date ? new Date(date) : new Date();
  });

  actionsModel = this.store.actionsModel;

  patchActionsModel(data: any) {
    this.store.patchActionsModel(data);
  }

  private planDialogSub?: Subscription;

  private openPlanningDialog(): void {
    const currentDeliveryId = this.route.snapshot.queryParams['deliveryId'];
    if (currentDeliveryId) {
      this.patchActionsModel({ deliveryId: currentDeliveryId });
    }
    confirmDialog({
      header: 'Naplánovať objednávku',
      form: {
        model: this.actionsModel(),
        onModelChange: (model) => this.patchActionsModel(model),
        loading: this.store.loading(),
        fields: formBuilder([
          selectLazyDeliveries(this.deliveriesService)
            .button({
              button: this.ability().can('create', 'Delivery')
                ? {
                    icon: 'pi pi-plus',
                    text: true,
                    tooltip: 'Pridať nové dodanie',
                    command: () =>
                      this.router.navigate(['create-delivery'], {
                        relativeTo: this.route,
                      }),
                  }
                : undefined,
            })
            .required(),
        ]),
        onSubmit: (model) =>
          this.store.processStep(this.model()!.id, {
            ...model,
            status: this.model()!.status,
          }),
      },
    });
  }

  headerButtons: Signal<HeaderButton[]> = computed(() => [
    {
      label: 'Potvrdiť',
      icon: 'pi pi-check',
      severity: 'primary',
      outlined: true,
      visible: this.model()?.status === OrderStatus.draft && this.isEdit(),
      action: () =>
        confirmDialog({
          header: 'Potvrdiť objednávku',
          message: 'Naozaj chcete potvrdiť objednávku?',
          icon: 'pi pi-check-circle',
          accept: () =>
            this.store.processStep(this.model()!.id, {
              status: this.model()!.status,
            }),
        }),
    },
    {
      label: 'Naplánovať',
      icon: 'pi pi-calendar',
      severity: 'primary',
      outlined: true,
      visible:
        this.model()?.status === OrderStatus.approved &&
        this.model()?.type !== FlowOrderType.storeSale &&
        this.isEdit(),
      action: () => {
        this.planDialogSub?.unsubscribe();
        this.openPlanningDialog();
        this.planDialogSub = this.route.queryParams
          .pipe(
            skip(1),
            filter((params) => !!params['deliveryId']),
            take(1),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((params) => {
            this.patchActionsModel({ deliveryId: params['deliveryId'] });
            this.openPlanningDialog();
          });
      },
    },
    {
      label: 'Spracovať predaj',
      icon: 'pi pi-truck',
      severity: 'primary',
      outlined: true,
      visible:
        this.model()?.status === OrderStatus.approved &&
        this.model()?.type === FlowOrderType.storeSale &&
        this.isEdit(),
      action: () =>
        this.router.navigate(['selection'], { relativeTo: this.route }),
    },
    {
      label: 'Naložiť',
      icon: 'pi pi-truck',
      severity: 'primary',
      outlined: true,
      visible: this.model()?.status === OrderStatus.planned && this.isEdit(),
      action: () =>
        this.router.navigate(['selection'], { relativeTo: this.route }),
    },
    {
      label: 'Vyložiť',
      icon: 'pi pi-truck',
      severity: 'primary',
      outlined: true,
      visible: this.model()?.status === OrderStatus.delivery && this.isEdit(),
      action: () =>
        this.router.navigate(['selection'], { relativeTo: this.route }),
    },
    {
      label: 'Zvoz',
      icon: 'pi pi-truck',
      severity: 'primary',
      outlined: true,
      visible: this.model()?.status === OrderStatus.delivered && this.isEdit(),
      action: () =>
        this.router.navigate(['selection'], { relativeTo: this.route }),
    },
    {
      label: 'Vyloženie na sklade',
      icon: 'pi pi-check-circle',
      severity: 'success',
      outlined: true,
      visible: this.model()?.status === OrderStatus.collection && this.isEdit(),
      action: () =>
        this.router.navigate(['selection'], { relativeTo: this.route }),
    },
  ]);
}
